import type { DeParaTarget, GanttProDePara, Placement, StageFloorKey } from './de-para';

/**
 * O de-para do Park Europa, Torre 2 (Fase 25, D1 a D3).
 *
 * Portado de `integra.py` — o script que juntou o cronograma, o levantamento e a CPU em
 * 01/10/2026 —, **sem o levantamento e sem a CPU**: o GanttPRO não traz quantidade, e a
 * quantidade entra pelo modelo de planilha (ADR-161). Fica o que decide a linha: em que
 * disciplina e serviço do catálogo cada barra do cronograma cai, e onde.
 *
 * É **dado**. Para acrescentar um serviço, uma linha aqui; o resolvedor não muda.
 *
 * ## Os 16 serviços em aprovação (D3)
 *
 * Os marcados `pendingApproval` **não existem** no catálogo da obra e só entram depois de o
 * usuário aprovar um a um — 14 em disciplina que já existe e 2 com disciplina nova
 * (`newDiscipline`: AR CONDICIONADO e ELEVADOR). A aprovação grava-se aqui: tirar a marca
 * quando o serviço estiver no catálogo.
 *
 * O corrimão da escada de incêndio **não** é um desses serviços: o usuário o juntou a
 * ESQUADRIAS › GUARDA-CORPO (`joinsSameItem`), e as duas tarefas viram um item só por pavimento.
 *
 * ## Os níveis dos pavimentos de etapa
 *
 * Preliminar −3 e Fundação −2, os da Fase 23 (`STAGE_FLOORS`). A planilha de 01/10 usava
 * −2 e −1, que é o que o script assumia antes de a Fase 23 ser conferida — o de-para vale.
 */

const FROM_NAME: Placement = { kind: 'TASK_FLOOR' };
const EVERY_FLOOR: Placement = { kind: 'EVERY_FLOOR' };
const LAST_FLOOR: Placement = { kind: 'LAST_FLOOR' };
const floor = (level: number): Placement => ({ kind: 'FLOOR', level });
const stage = (key: StageFloorKey): Placement => ({ kind: 'STAGE', stage: key });

type Extras = Partial<
  Pick<
    DeParaTarget,
    | 'placement'
    | 'segment'
    | 'fromStructure'
    | 'pendingApproval'
    | 'newDiscipline'
    | 'note'
    | 'joinsSameItem'
    | 'laborWeight'
    | 'executionStep'
  >
>;

/** Um serviço do catálogo. */
function service(
  disciplineName: string,
  serviceName: string,
  unit: string | null,
  extras: Extras = {},
): DeParaTarget {
  return { disciplineName, serviceName, unit, placement: FROM_NAME, ...extras };
}

const PENDING = { pendingApproval: true } as const;
const PENDING_DISCIPLINE = { pendingApproval: true, newDiscipline: true } as const;

/** D1: cada barra de estrutura vira a laje e a escada do pavimento (02/10/2026: sem pilar nem viga). */
const STRUCTURE = { fromStructure: true } as const;

/** A unidade de cada serviço das peças, a do catálogo (`stage-registry.ts`). */
const PIECE_UNIT: Readonly<Record<string, string>> = {
  'CONFECÇÃO DE FÔRMA': 'm²',
  'MONTAGEM DE FÔRMA': 'm²',
  'DESMONTAGEM DE FÔRMA': 'm²',
  ESCORAMENTO: 'm²',
  CONCRETAGEM: 'm³',
  'INSTALAÇÃO DE FERRAGEM': 'kg',
  'MOVIMENTAÇÃO DE TERRA': 'm³',
  COMPACTAÇÃO: 'm²',
  'LASTRO DE CONCRETO': 'm³',
};

/**
 * D5 (01/10/2026): a barra que cobre várias peças vira **peça × serviço**, e o HH dela se
 * reparte pelo HH próprio da CPU. Cada peça leva a sua parte da barra — provisório, até haver o
 * quantitativo estrutural —, e dentro da peça os pesos se renormalizam sem os serviços que
 * ela não tem. Peso 0 é a armação, que é de empreiteiro: o item existe, com a janela da barra,
 * e não gasta HH próprio.
 */
function piecesByLabor(
  pieces: Readonly<Record<string, number>>,
  weights: Readonly<Record<string, number>>,
  steps: Readonly<Record<string, number>>,
  extras: Extras = {},
  without: Readonly<Record<string, readonly string[]>> = {},
): DeParaTarget[] {
  return Object.entries(pieces).flatMap(([piece, share]) => {
    const services = Object.entries(weights).filter(
      ([name]) => !(without[piece] ?? []).includes(name),
    );
    const total = services.reduce((acc, [, weight]) => acc + weight, 0);
    return services.map(([name, weight]) =>
      service(piece, name, PIECE_UNIT[name] ?? null, {
        ...extras,
        laborWeight: total > 0 ? (weight / total) * share : 0,
        ...(steps[name] === undefined ? {} : { executionStep: steps[name] }),
      }),
    );
  });
}

/**
 * Estrutura: Q da obra × hh/un das CPU 4.2.1 (confecção), 4.2.2 (montagem), 4.2.3 (desmontagem,
 * com a área de montagem), 4.1.1 (escoramento) e 4.5.5 (concreto). A 4.3.1 (armação) é de
 * empreiteiro e não tem HH próprio.
 */
const STRUCTURE_LABOR = {
  'CONFECÇÃO DE FÔRMA': 7.49,
  'MONTAGEM DE FÔRMA': 53.26,
  'DESMONTAGEM DE FÔRMA': 11.72,
  ESCORAMENTO: 3.67,
  CONCRETAGEM: 23.86,
  'INSTALAÇÃO DE FERRAGEM': 0,
} as const;

/**
 * RN-329 (02/10/2026): a ordem de execução da estrutura. O escoramento vem antes da montagem da
 * fôrma de viga e laje. A armação, sem HH, fica com a janela da montagem.
 */
const STRUCTURE_STEPS = {
  'CONFECÇÃO DE FÔRMA': 1,
  ESCORAMENTO: 2,
  'MONTAGEM DE FÔRMA': 3,
  'INSTALAÇÃO DE FERRAGEM': 4,
  CONCRETAGEM: 5,
  'DESMONTAGEM DE FÔRMA': 6,
} as const;

/**
 * Bloco e viga baldrame (18.779 HH na barra): o que a CPU mede — lastro 3.2.4, fôrma 3.2.6.x e
 * concreto 3.2.14, × 169/481 — é 3.793 HH; o resto é terra, porque escavação, reaterro e
 * compactação (3.2.1 a 3.2.3) só têm mão de obra e o orçamento não diz a quantidade deles. O
 * resto se divide pelo hh/un: escavação + reaterro 6,25 contra compactação 1,5, com vala de ~1 m.
 */
const FOUNDATION_LABOR = {
  'MOVIMENTAÇÃO DE TERRA': 64.36,
  COMPACTAÇÃO: 15.45,
  CONCRETAGEM: 6.99,
  'CONFECÇÃO DE FÔRMA': 5.93,
  'MONTAGEM DE FÔRMA': 4.77,
  'DESMONTAGEM DE FÔRMA': 2.04,
  'LASTRO DE CONCRETO': 0.45,
  'INSTALAÇÃO DE FERRAGEM': 0,
} as const;

/**
 * RN-329: a ordem do bloco e da viga baldrame. Primeiro a escavação (CPU 3.2.1), depois a
 * compactação do fundo da vala (3.2.2) e o lastro (3.2.4). O reaterro (3.2.3) também é
 * MOVIMENTAÇÃO DE TERRA, e fica no começo com a escavação.
 */
const FOUNDATION_STEPS = {
  'MOVIMENTAÇÃO DE TERRA': 1,
  COMPACTAÇÃO: 2,
  'LASTRO DE CONCRETO': 3,
  'CONFECÇÃO DE FÔRMA': 4,
  'MONTAGEM DE FÔRMA': 5,
  'INSTALAÇÃO DE FERRAGEM': 6,
  CONCRETAGEM: 7,
  'DESMONTAGEM DE FÔRMA': 8,
} as const;

export const PARK_EUROPA_DE_PARA: GanttProDePara = {
  byGroup: [
    // ── Estrutura: uma barra por pavimento vira a LAJE e a ESCADA (D1, 02/10/2026) ────
    {
      group: 'ESTRUTURA',
      service: 'CONVENCIONAL',
      // 02/10/2026: a obra é de alvenaria estrutural — não há pilar nem viga. A laje leva o ¾
      // que era de pilares, vigas e laje; a escada, o ¼ que já tinha (provisório).
      targets: piecesByLabor(
        { LAJE: 0.75, ESCADAS: 0.25 },
        STRUCTURE_LABOR,
        STRUCTURE_STEPS,
        STRUCTURE,
      ),
    },

    // ── Alvenaria ────────────────────────────────────────────────────────────────────
    {
      group: 'ALVENARIA',
      service: 'ESTRUTURAL',
      targets: [service('ALVENARIA', 'ALVENARIA ESTRUTURAL', 'm²')],
    },
    {
      group: 'ALVENARIA',
      service: 'VEDAÇÃO',
      targets: [service('ALVENARIA', 'ALVENARIA NÃO ESTRUTURAL', 'm²')],
    },

    // ── Instalações ──────────────────────────────────────────────────────────────────
    {
      group: 'INSTALAÇÃO HIDRÁULICA',
      service: 'PRUMADA & DISTRIBUIÇÃO',
      targets: [service('INSTALACOES HIDROSSANITARIAS', 'PRUMADA E DISTRIBUIÇÃO', 'vb', PENDING)],
    },
    {
      group: 'INSTALAÇÃO HIDRÁULICA',
      service: 'LOUÇAS',
      targets: [service('LOUÇAS E METAIS', 'LOUÇAS', 'un')],
    },
    {
      group: 'INSTALAÇÃO HIDRÁULICA',
      service: 'METAIS',
      targets: [service('LOUÇAS E METAIS', 'METAIS', 'un')],
    },
    {
      group: 'INSTALAÇÃO ESPECIAL',
      service: 'GÁS',
      targets: [service('INSTALACOES DE GAS', 'INSTALAÇÃO A GÁS', 'un')],
    },
    {
      group: 'INSTALAÇÃO ESPECIAL',
      service: 'AR CONDICIONADO',
      targets: [
        service('AR CONDICIONADO', 'INSTALAÇÃO DE AR CONDICIONADO', 'un', PENDING_DISCIPLINE),
      ],
    },
    {
      group: 'INSTALAÇÃO ESPECIAL',
      service: 'PREVENÇÃO E COMBATE A INCÊNDIO',
      targets: [service('PSCIP', 'INFRAESTRUTURA', 'un')],
    },
    {
      group: 'INSTALAÇÃO ELÉTRICA',
      service: 'TUBULAÇÃO',
      targets: [service('INSTALACOES ELETRICAS E DADOS', 'INFRAESTRUTURA E ALIMENTAÇÃO', 'un')],
    },
    {
      group: 'INSTALAÇÃO ELÉTRICA',
      service: 'ENFIAÇÃO',
      targets: [service('INSTALACOES ELETRICAS E DADOS', 'CABEAMENTO', 'un')],
    },
    {
      group: 'INSTALAÇÃO ELÉTRICA',
      service: 'FECHAMENTO DE QUADROS',
      targets: [service('INSTALACOES ELETRICAS E DADOS', 'DISJUNTORES', 'un')],
    },
    {
      group: 'INSTALAÇÃO ELÉTRICA',
      service: 'ACABAMENTO',
      targets: [service('INSTALACOES ELETRICAS E DADOS', 'PLACA + ACABAMENTO DAS CAIXINHAS', 'un')],
    },

    // ── Esquadrias ───────────────────────────────────────────────────────────────────
    {
      group: 'ESQUADRIA DE MADEIRA',
      service: 'INTERNA',
      targets: [service('ESQUADRIAS', 'PORTAS', 'un')],
    },
    {
      group: 'ESQUADRIA METÁLICA',
      service: 'PORTA, JANELA E FIXO',
      targets: [service('ESQUADRIAS', 'JANELAS E PORTAS DE VIDRO', 'un')],
    },

    // ── Revestimento interno, forro e gesso ─────────────────────────────────────────
    {
      group: 'REVESTIMENTO INTERNO',
      service: 'EMBOÇO',
      targets: [service('REVESTIMENTO INTERNO', 'EMBOÇO CONVENCIONAL', 'm²')],
    },
    {
      // D5: a barra do azulejo vira banheiro e cozinha, pela área do levantamento (3.178,9 e
      // 1.721,8 m²) — as CPU 12.3.1 e 12.3.2 têm o mesmo hh/m². Um banheiro por apartamento.
      group: 'REVESTIMENTO INTERNO',
      service: 'AZULEJO',
      targets: [
        service('ASSENTAMENTO DE AZULEJO', 'BANHEIRO SOCIAL', 'm²', { laborWeight: 0.6487 }),
        service('ASSENTAMENTO DE AZULEJO', 'COZINHA', 'm²', { laborWeight: 0.3513 }),
      ],
    },
    {
      // O gesso liso tem parede e teto no catálogo; a barra do cronograma é uma só.
      group: 'REVESTIMENTO INTERNO',
      service: 'GESSO LISO',
      targets: [
        service('REVESTIMENTO INTERNO', 'REBOCO GESSO PAREDE', 'm²'),
        service('REVESTIMENTO INTERNO', 'REBOCO GESSO TETO', 'm²'),
      ],
    },
    {
      group: 'FORRO DE GESSO',
      service: 'GESSO EM PLACA COM TABICA',
      targets: [service('FORROS E SHAFTS', 'FORRO EM PLACAS DE GESSO', 'm²')],
    },

    // ── Impermeabilização ────────────────────────────────────────────────────────────
    {
      group: 'IMPERMEABILIZAÇÃO',
      service: 'CRISTALIZAÇÃO',
      targets: [service('IMPERMEABILIZAÇÃO', 'CRISTALIZAÇÃO', 'm²', PENDING)],
    },
    {
      group: 'IMPERMEABILIZAÇÃO',
      service: 'MANTA',
      targets: [service('IMPERMEABILIZAÇÃO', 'MANTA', 'm²', PENDING)],
    },

    // ── Pavimentação interna ─────────────────────────────────────────────────────────
    {
      group: 'PAVIMENTAÇÃO INTERNA',
      service: 'CONTRAPISO',
      targets: [service('PAVIMENTACAO, PISOS E LAMINADOS', 'CONTRAPISO', 'm²')],
    },
    {
      group: 'PAVIMENTAÇÃO INTERNA',
      service: 'PORCELANATO',
      targets: [service('PAVIMENTACAO, PISOS E LAMINADOS', 'PORCELANATO', 'm²')],
    },
    {
      group: 'PAVIMENTAÇÃO INTERNA',
      service: 'PISO LAMINADO',
      targets: [service('PAVIMENTACAO, PISOS E LAMINADOS', 'LAMINADO', 'm²')],
    },
    {
      group: 'PAVIMENTAÇÃO INTERNA',
      service: 'CIMENTADO LISO',
      targets: [service('PAVIMENTACAO, PISOS E LAMINADOS', 'CIMENTADO LISO', 'm²', PENDING)],
    },
    {
      group: 'PAVIMENTAÇÃO INTERNA',
      service: 'RODAPÉ CIMENTADO',
      targets: [service('PAVIMENTACAO, PISOS E LAMINADOS', 'RODAPÉ CIMENTADO', 'm', PENDING)],
    },
    {
      // 02/10/2026: o rodapé de PVC e o de porcelanato são um serviço só, RODAPÉ. As duas barras
      // de cada pavimento (mesma janela) viram um item, com o HH e a quantidade somados.
      group: 'PAVIMENTAÇÃO INTERNA',
      service: 'RODAPÉ PVC',
      targets: [service('PAVIMENTACAO, PISOS E LAMINADOS', 'RODAPÉ', 'm', PENDING)],
    },
    {
      group: 'PAVIMENTAÇÃO INTERNA',
      service: 'RODAPÉ PORCELANATO',
      targets: [
        service('PAVIMENTACAO, PISOS E LAMINADOS', 'RODAPÉ', 'm', {
          ...PENDING,
          joinsSameItem: true,
        }),
      ],
    },

    // ── Fachada e cobertura: sem pavimento no nome, divididas pelos pavimentos (D2) ──
    {
      group: 'REVESTIMENTO EXTERNO',
      service: 'EMBOÇO',
      targets: [service('REVESTIMENTO EXTERNO', 'ARGAMASSA', 'm²', { placement: EVERY_FLOOR })],
    },
    {
      group: 'REVESTIMENTO EXTERNO',
      service: 'PINTURA ACRÍLICA FACHADA',
      targets: [service('PINTURA EXTERNA', 'FACHADA', 'm²', { placement: EVERY_FLOOR })],
    },
    {
      // A cobertura metálica fica no último pavimento do arquivo.
      group: 'COBERTURA',
      service: 'METÁLICA',
      targets: [service('COBERTURA', 'TELHADO', 'm²', { placement: LAST_FLOOR })],
    },

    // ── Pintura ──────────────────────────────────────────────────────────────────────
    {
      group: 'PINTURA',
      service: '1ª DEMÃO COM EMASSAMENTO',
      targets: [service('PINTURA INTERNA', '1ª DEMÃO COM EMASSAMENTO', 'm²', PENDING)],
    },
    {
      group: 'PINTURA',
      service: '2ª DEMÃO',
      targets: [service('PINTURA INTERNA', '2ª DEMÃO', 'm²', PENDING)],
    },

    // ── Soleira, peitoril, chapim e bancada ──────────────────────────────────────────
    {
      group: 'SOLEIRA / PEITORIL / FILETE / CHAPIM / BANCADA',
      service: 'SOLEIRA E FILETE EM GRANITO',
      targets: [service('PEDRAS E GRANITOS', 'SOLEIRAS', 'm')],
    },
    {
      group: 'SOLEIRA / PEITORIL / FILETE / CHAPIM / BANCADA',
      service: 'PEITORIL EM GRANITO',
      targets: [service('PEDRAS E GRANITOS', 'PEITORIL', 'm')],
    },
    {
      group: 'SOLEIRA / PEITORIL / FILETE / CHAPIM / BANCADA',
      service: 'BANCADA EM GRANITO',
      targets: [
        service('PEDRAS E GRANITOS', 'BANCADAS', 'm²', {
          note: 'O catálogo mede BANCADAS em un; o levantamento da Torre 2 vem em m².',
        }),
      ],
    },
  ],

  byTaskName: [
    // ── Fundação: o nome é "Fundação / <serviço>", e o serviço vira a frente da etapa (D1) ─
    {
      taskName: 'Fundação / Execução de Gabarito',
      targets: [service('LOCAÇÃO DA OBRA', 'GABARITO', 'm', { placement: stage('PRELIMINAR') })],
    },
    {
      taskName: 'Fundação / Estaca Hélice Contínua Monitorada',
      // A hélice contínua é perfuração e concreto de uma vez, do empreiteiro (CPU 3.1.2).
      targets: [service('ESTACAS', 'CONCRETAGEM', 'm³', { placement: stage('FUNDACAO') })],
    },
    {
      // Uma barra, duas frentes: o bloco e a viga baldrame têm a mesma janela.
      taskName: 'Fundação / Bloco de Coroamento & Viga de Cintamento',
      targets: piecesByLabor(
        { BLOCO: 0.5, 'VIGAS BALDRAME': 0.5 },
        FOUNDATION_LABOR,
        FOUNDATION_STEPS,
        {
          placement: stage('FUNDACAO'),
        },
      ),
    },
    {
      taskName: 'Fundação / Radier',
      // A fôrma do radier é só a borda (3.2.7, 6% do HH): vai junto com o concreto (3.2.13).
      targets: [
        // RN-329: a armação vem antes do concreto, mas não tem HH, e fica com a janela dele.
        service('LAJE DE FUNDAÇÃO', 'CONCRETAGEM', 'm³', {
          placement: stage('FUNDACAO'),
          laborWeight: 1,
          executionStep: 2,
        }),
        service('LAJE DE FUNDAÇÃO', 'INSTALAÇÃO DE FERRAGEM', 'kg', {
          placement: stage('FUNDACAO'),
          laborWeight: 0,
          executionStep: 1,
        }),
      ],
    },
    {
      taskName: 'Impermeabilização - Baldrame',
      targets: [
        service('VIGAS BALDRAME', 'IMPERMEABILIZAÇÃO', 'm²', { placement: stage('FUNDACAO') }),
      ],
    },

    // ── Esquadrias sem pavimento: um item por pavimento (D2) ─────────────────────────
    {
      taskName: 'Esquadria Metálica - Porta Corta Fogo',
      targets: [
        service('ESQUADRIAS', 'PORTA CORTA FOGO', 'un', { ...PENDING, placement: EVERY_FLOOR }),
      ],
    },
    {
      taskName: 'Esquadria Metálica - Guarda-Corpo Escada de Incêndio',
      targets: [service('ESQUADRIAS', 'GUARDA-CORPO', 'un', { placement: EVERY_FLOOR })],
    },
    {
      // Sem serviço próprio (D3): cai no GUARDA-CORPO, com as mesmas datas e a mesma antecessora.
      taskName: 'Esquadria Metálica - Corrimão Escada de Incêndio',
      targets: [
        service('ESQUADRIAS', 'GUARDA-CORPO', 'un', {
          placement: EVERY_FLOOR,
          joinsSameItem: true,
        }),
      ],
    },

    // ── Os "diversos" ────────────────────────────────────────────────────────────────
    {
      // O local vem escrito "14º Barrilete", sem a palavra Pavimento.
      taskName: 'Revestimento Interno / Regularização de Alvenaria / 14º Barrilete',
      targets: [service('REVESTIMENTO INTERNO', 'REGULARIZAÇÃO DE ALVENARIA', 'm²', PENDING)],
    },
    {
      taskName: 'Pintura - Esmalte sobre Ferro',
      targets: [
        service('PINTURA INTERNA', 'ESMALTE SOBRE FERRO', 'm²', {
          ...PENDING,
          placement: EVERY_FLOOR,
        }),
      ],
    },
    {
      // Área externa e garagem: o 1º pavimento (D2).
      taskName: 'Pintura - Demarcação de Vaga & Faixa Reflexiva',
      targets: [
        service('PINTURA EXTERNA', 'DEMARCAÇÃO DE VAGA E FAIXA REFLEXIVA', 'm', {
          ...PENDING,
          placement: floor(1),
        }),
      ],
    },
    {
      // Os muros: o trecho é MUROS, e o pavimento é a Fundação (02/10/2026) — muro e muro de
      // contenção são da Fundação, e o chapim do muro vai junto.
      taskName: 'Chapim em Granito / Muros 1º Pavimento',
      targets: [
        service('PEDRAS E GRANITOS', 'CHAPIM', 'm', {
          ...PENDING,
          placement: stage('FUNDACAO'),
          segment: 'MUROS',
        }),
      ],
    },
    {
      // "Barrilete" sem número: é o 14º, o barrilete que o resto do arquivo numera.
      taskName: 'Chapim em Granito / Barrilete',
      targets: [service('PEDRAS E GRANITOS', 'CHAPIM', 'm', { ...PENDING, placement: floor(14) })],
    },
    {
      taskName: 'Elevador',
      targets: [
        service('ELEVADOR', 'INSTALAÇÃO DE ELEVADOR', 'un', {
          ...PENDING_DISCIPLINE,
          placement: EVERY_FLOOR,
        }),
      ],
    },
  ],
};
